window.AASupabaseClient = (() => {
  let client = null;

  function init() {
    if (client) return client;

    if (!window.supabase) {
      console.error('Supabase library is not loaded.');
      return null;
    }

    const config = window.AA_SUPABASE_CONFIG;

    if (!config || !config.url || !config.anonKey) {
      console.warn('Supabase config is missing.');
      return null;
    }

    client = window.supabase.createClient(config.url, config.anonKey);
    return client;
  }

  function getClient() {
    return client || init();
  }

  function getPlaybookServiceMethod(methodName) {
    const service = window.AAPlaybookService;

    if (!service || typeof service[methodName] !== 'function') {
      throw new Error(
        `AAPlaybookService.${methodName}() is not available.`
      );
    }

    return service[methodName];
  }

  async function getClients() {
    return getPlaybookServiceMethod('getClients')();
  }

  async function createClient(payload = {}) {
  const result = await getPlaybookServiceMethod('createClient')({
    businessName: payload.businessName || payload.business_name
  });

  if (!result?.success || !result?.client) {
    throw result?.error || new Error('Unable to create client.');
  }

  return result.client;
}

async function updateClient(payload = {}) {
  const updates = payload.updates || payload;

  const result = await getPlaybookServiceMethod('updateClient')({
    id: payload.id || payload.clientId,
    businessName: updates.businessName ?? updates.business_name,
    activeStatus: updates.activeStatus ?? updates.active_status,
    accountStatus: updates.accountStatus ?? updates.account_status,
    invoiceStatus: updates.invoiceStatus ?? updates.invoice_status
  });

  if (!result?.success) {
    throw result?.error || new Error('Unable to update client.');
  }

  return result.client;
}

async function archiveClient(payload = {}) {
  const success = await getPlaybookServiceMethod('archiveClient')({
    id: payload.id || payload.clientId
  });

  if (!success) {
    throw new Error('Unable to archive client.');
  }

  return true;
}

  async function getClientConfiguration(payload) {
    return getPlaybookServiceMethod('getClientConfiguration')(payload);
  }

  async function saveClientConfiguration(payload) {
    return getPlaybookServiceMethod('saveClientConfiguration')(payload);
  }

  async function getClientAccount(payload) {
  return getPlaybookServiceMethod('getClientAccount')(payload);
}

async function deleteClientTemplateAssignment({
  assignmentId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!assignmentId) {
    throw new Error('Template assignment id is required.');
  }

  const { data: assignment, error: lookupError } = await db
    .from('aa_client_template_assignments')
    .select('id, assignment_status')
    .eq('id', assignmentId)
    .single();

  if (lookupError) {
    throw lookupError;
  }

  const status = String(
    assignment?.assignment_status || ''
  )
    .trim()
    .toLowerCase();

  if (status === 'generated') {
    throw new Error(
      'This template is in use and cannot be deleted.'
    );
  }

  const { error } = await db
    .from('aa_client_template_assignments')
    .delete()
    .eq('id', assignmentId);

  if (error) {
    throw error;
  }

  return true;
}

async function updateClientTemplateAssignmentStatus({
  assignmentId,
  status
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!assignmentId) {
    throw new Error('Template assignment id is required.');
  }

  if (!status) {
    throw new Error('Template assignment status is required.');
  }

  const { data, error } = await db
    .from('aa_client_template_assignments')
    .update({
      assignment_status: status,
      updated_at: new Date().toISOString()
    })
    .eq('id', assignmentId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}
function normalizeCatalogMonth(value) {
  if (!value) {
    throw new Error('Catalog month is required.');
  }

  const raw = String(value).trim();

  const monthMatch = raw.match(/^(\d{4})-(\d{2})/);

  if (!monthMatch) {
    throw new Error('Catalog month must be a valid year and month.');
  }

  return `${monthMatch[1]}-${monthMatch[2]}-01`;
}

async function uploadCatalogCover({
  clientId,
  file
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!clientId) {
    throw new Error('Client id is required.');
  }

  if (!file) {
    throw new Error('Catalog cover file is required.');
  }

  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/webp'
  ];

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      'Catalog cover must be a JPG, PNG, or WebP image.'
    );
  }

  const maxFileSize = 10 * 1024 * 1024;

  if (file.size > maxFileSize) {
    throw new Error(
      'Catalog cover must be 10 MB or smaller.'
    );
  }

  const extension =
    file.name.split('.').pop()?.toLowerCase() || 'jpg';

  const safeClientId = String(clientId)
    .replace(/[^a-zA-Z0-9-]/g, '');

  const fileName =
    `${safeClientId}/${Date.now()}-${crypto.randomUUID()}.${extension}`;

  const { error: uploadError } = await db.storage
    .from('catalog-covers')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type
    });

  if (uploadError) {
    throw uploadError;
  }

  const { data } = db.storage
    .from('catalog-covers')
    .getPublicUrl(fileName);

  if (!data?.publicUrl) {
    throw new Error(
      'Catalog cover uploaded but no public URL was returned.'
    );
  }

  return {
    path: fileName,
    publicUrl: data.publicUrl
  };
}

async function uploadPlaybookFile({
  clientId,
  playbookId,
  fieldId,
  file
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!clientId) {
    throw new Error(
      'Client id is required.'
    );
  }

  if (!playbookId) {
    throw new Error(
      'Playbook id is required.'
    );
  }

  if (!fieldId) {
    throw new Error(
      'Field id is required.'
    );
  }

  if (!file) {
    throw new Error(
      'Playbook file is required.'
    );
  }

  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/pdf'
  ];

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      'File must be a JPG, PNG, WebP, or PDF.'
    );
  }

  const maxFileSize =
    20 * 1024 * 1024;

  if (file.size > maxFileSize) {
    throw new Error(
      'File must be 20 MB or smaller.'
    );
  }

  const extension =
    file.name
      .split('.')
      .pop()
      ?.toLowerCase() ||
    'file';

  const safeClientId =
    String(clientId)
      .replace(
        /[^a-zA-Z0-9-]/g,
        ''
      );

  const safePlaybookId =
    String(playbookId)
      .replace(
        /[^a-zA-Z0-9-]/g,
        ''
      );

  const safeFieldId =
    String(fieldId)
      .replace(
        /[^a-zA-Z0-9_-]/g,
        '-'
      );

  const fileName =
    `${safeClientId}/` +
    `${safePlaybookId}/` +
    `${safeFieldId}/` +
    `${Date.now()}-${crypto.randomUUID()}.${extension}`;

  const { error: uploadError } =
    await db.storage
      .from('playbook-files')
      .upload(
        fileName,
        file,
        {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        }
      );

  if (uploadError) {
    throw uploadError;
  }

  return {
    path: fileName,
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size
  };
}

async function getPlaybookFileSignedUrl({
  path,
  expiresIn = 3600
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!path) {
    throw new Error(
      'Playbook file path is required.'
    );
  }

  const {
    data,
    error
  } = await db.storage
    .from('playbook-files')
    .createSignedUrl(
      path,
      expiresIn
    );

  if (error) {
    throw error;
  }

  if (!data?.signedUrl) {
    throw new Error(
      'Unable to create file access URL.'
    );
  }

  return data.signedUrl;
}

async function deletePlaybookFile({
  path
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!path) {
    throw new Error(
      'Playbook file path is required.'
    );
  }

  const { error } =
    await db.storage
      .from('playbook-files')
      .remove([path]);

  if (error) {
    throw error;
  }

  return true;
}

async function getCatalogRecords({
  clientId,
  year,
  startDate,
  endDate,
  brand
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!clientId) {
    throw new Error('Client id is required.');
  }

  let query = db
    .from('aa_catalog_records')
    .select('*')
    .eq('client_id', clientId)
    .eq('status', 'active')
    .order('catalog_month', { ascending: false });

  if (year) {
    const numericYear = Number(year);

    if (!Number.isInteger(numericYear)) {
      throw new Error('Catalog year must be valid.');
    }

    query = query
      .gte('catalog_month', `${numericYear}-01-01`)
      .lte('catalog_month', `${numericYear}-12-01`);
  }

  if (startDate) {
    query = query.gte(
      'catalog_month',
      normalizeCatalogMonth(startDate)
    );
  }

 if (endDate) {
  query = query.lte(
    'catalog_month',
    normalizeCatalogMonth(endDate)
  );
}

if (brand) {
  query = query.filter(
    'brands',
    'cs',
    JSON.stringify([
      String(brand).trim()
    ])
  );
}

const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}

async function getCatalogRecord({ id } = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!id) {
    throw new Error('Catalog record id is required.');
  }

  const { data, error } = await db
    .from('aa_catalog_records')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function createCatalogRecord(payload = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!payload.client_id && !payload.clientId) {
    throw new Error('Client id is required.');
  }

  if (!payload.catalog_month && !payload.catalogMonth) {
    throw new Error('Catalog month is required.');
  }

  const clientId =
    payload.client_id ||
    payload.clientId;

  const catalogMonth = normalizeCatalogMonth(
    payload.catalog_month ||
    payload.catalogMonth
  );

  const { data: existingRecords, error: duplicateError } =
    await db
      .from('aa_catalog_records')
      .select('id, catalog_title, catalog_month')
      .eq('client_id', clientId)
      .eq('catalog_month', catalogMonth)
      .eq('status', 'active')
      .limit(1);

  if (duplicateError) {
    throw duplicateError;
  }

  if (existingRecords?.length) {
    const duplicate = existingRecords[0];

    const error = new Error(
      'An active catalog already exists for this client and month. Edit the existing catalog instead.'
    );

    error.code = 'AA_DUPLICATE_CATALOG_MONTH';
    error.catalogRecord = duplicate;

    throw error;
  }

  const record = {
    client_id: clientId,
    catalog_month: catalogMonth,
    publication_date:
      payload.publication_date ??
      payload.publicationDate ??
      null,
    catalog_title:
      payload.catalog_title ??
      payload.catalogTitle ??
      '',
    catalog_url:
      payload.catalog_url ??
      payload.catalogUrl ??
      '',
    cover_image_url:
  payload.cover_image_url ??
  payload.coverImageUrl ??
  null,
heyzine_flipbook_id:
  payload.heyzine_flipbook_id ??
  payload.heyzineFlipbookId ??
  null,
catalog_views:
      payload.catalog_views ??
      payload.catalogViews ??
      null,
    brands: Array.isArray(payload.brands)
      ? payload.brands
      : [],
    status: 'active',
    updated_at: new Date().toISOString()
  };

  const { data, error } = await db
    .from('aa_catalog_records')
    .insert(record)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function updateCatalogRecord({
  id,
  updates = {}
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!id) {
    throw new Error('Catalog record id is required.');
  }

  const payload = {
    ...updates,
    updated_at: new Date().toISOString()
  };

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      'catalogMonth'
    )
  ) {
    payload.catalog_month = normalizeCatalogMonth(
      payload.catalogMonth
    );

    delete payload.catalogMonth;
  }

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      'catalog_month'
    )
  ) {
    payload.catalog_month = normalizeCatalogMonth(
      payload.catalog_month
    );
  }

  const camelCaseFields = {
    publicationDate: 'publication_date',
    catalogTitle: 'catalog_title',
    catalogUrl: 'catalog_url',
   coverImageUrl: 'cover_image_url',
heyzineFlipbookId: 'heyzine_flipbook_id',
catalogViews: 'catalog_views'
  };

  Object.entries(camelCaseFields).forEach(
    ([source, destination]) => {
      if (
        Object.prototype.hasOwnProperty.call(
          payload,
          source
        )
      ) {
        payload[destination] = payload[source];
        delete payload[source];
      }
    }
  );

  const { data, error } = await db
    .from('aa_catalog_records')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function archiveCatalogRecord({ id } = {}) {
  return updateCatalogRecord({
    id,
    updates: {
      status: 'archived'
    }
  });
}

async function getPreviousMonthCatalog({
  clientId,
  referenceMonth
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!clientId) {
    throw new Error('Client id is required.');
  }

  if (!referenceMonth) {
    throw new Error('Reference month is required.');
  }

  const normalizedReferenceMonth =
    normalizeCatalogMonth(referenceMonth);

  const [year, month] =
    normalizedReferenceMonth
      .split('-')
      .map(Number);

  const previousDate = new Date(
    Date.UTC(year, month - 2, 1)
  );

  const previousMonth =
    `${previousDate.getUTCFullYear()}-` +
    `${String(
      previousDate.getUTCMonth() + 1
    ).padStart(2, '0')}-01`;

  const { data, error } = await db
    .from('aa_catalog_records')
    .select(`
      id,
      client_id,
      catalog_month,
      publication_date,
      catalog_title,
      catalog_url,
      cover_image_url,
heyzine_flipbook_id,
catalog_views,
      brands,
      status
    `)
    .eq('client_id', clientId)
    .eq('catalog_month', previousMonth)
    .eq('status', 'active')
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data || null;
}


function normalizeEmailRequestStatus(status) {
  const normalized =
    String(status || 'draft')
      .trim()
      .toLowerCase();

  const allowedStatuses = [
    'draft',
    'submitted',
    'in_progress',
    'ready',
    'sent',
    'archived'
  ];

  if (!allowedStatuses.includes(normalized)) {
    throw new Error(
      'Email request status is invalid.'
    );
  }

  return normalized;
}

async function getEmailRequests({
  clientId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!clientId) {
    throw new Error(
      'Client id is required.'
    );
  }

  const { data, error } =
    await db
      .from('aa_email_requests')
      .select('*')
      .eq('client_id', clientId)
      .order(
        'email_date',
        { ascending: true }
      )
      .order(
        'created_at',
        { ascending: true }
      );

  if (error) {
    throw error;
  }

  return data || [];
}

async function getEmailRequest({
  id
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Email request id is required.'
    );
  }

  const { data, error } =
    await db
      .from('aa_email_requests')
      .select('*')
      .eq('id', id)
      .maybeSingle();

  if (error) {
    throw error;
  }

  return data || null;
}

async function createEmailRequest(
  payload = {}
) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const clientId =
    payload.clientId ||
    payload.client_id;

  const emailDate =
    payload.emailDate ||
    payload.email_date;

  const emailTopic =
    payload.emailTopic ||
    payload.email_topic ||
    payload.topic;

  if (!clientId) {
    throw new Error(
      'Client id is required.'
    );
  }

  if (!emailDate) {
    throw new Error(
      'Email date is required.'
    );
  }

  if (
    !emailTopic ||
    !String(emailTopic).trim()
  ) {
    throw new Error(
      'Email topic is required.'
    );
  }

  const status =
    normalizeEmailRequestStatus(
      payload.status || 'draft'
    );

  const record = {
    client_id: clientId,
    email_date: emailDate,
    email_topic:
      String(emailTopic).trim(),
    subject:
      payload.subject === undefined ||
      payload.subject === null ||
      String(payload.subject).trim() === ''
        ? null
        : String(payload.subject).trim(),
    preheader:
      payload.preheader === undefined ||
      payload.preheader === null ||
      String(payload.preheader).trim() === ''
        ? null
        : String(payload.preheader).trim(),
    notes:
      payload.notes === undefined ||
      payload.notes === null ||
      String(payload.notes).trim() === ''
        ? null
        : String(payload.notes).trim(),
    status,
    updated_at:
      new Date().toISOString()
  };

  const { data, error } =
    await db
      .from('aa_email_requests')
      .insert(record)
      .select()
      .single();

  if (error) {
    throw error;
  }

  return data;
}

async function updateEmailRequest({
  id,
  updates = {}
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Email request id is required.'
    );
  }

  const payload = {};

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'emailDate'
    )
  ) {
    payload.email_date =
      updates.emailDate;
  }

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'email_date'
    )
  ) {
    payload.email_date =
      updates.email_date;
  }

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'emailTopic'
    )
  ) {
    payload.email_topic =
      String(
        updates.emailTopic || ''
      ).trim();
  }

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'email_topic'
    )
  ) {
    payload.email_topic =
      String(
        updates.email_topic || ''
      ).trim();
  }

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'topic'
    )
  ) {
    payload.email_topic =
      String(
        updates.topic || ''
      ).trim();
  }

  [
    'subject',
    'preheader',
    'notes'
  ].forEach((field) => {
    if (
      Object.prototype.hasOwnProperty.call(
        updates,
        field
      )
    ) {
      const value =
        updates[field];

      payload[field] =
        value === undefined ||
        value === null ||
        String(value).trim() === ''
          ? null
          : String(value).trim();
    }
  });

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'status'
    )
  ) {
    payload.status =
      normalizeEmailRequestStatus(
        updates.status
      );
  }

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      'email_topic'
    ) &&
    !payload.email_topic
  ) {
    throw new Error(
      'Email topic is required.'
    );
  }

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      'email_date'
    ) &&
    !payload.email_date
  ) {
    throw new Error(
      'Email date is required.'
    );
  }

  payload.updated_at =
    new Date().toISOString();

  const { data, error } =
    await db
      .from('aa_email_requests')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

  if (error) {
    throw error;
  }

  return data;
}

async function archiveEmailRequest({
  id
} = {}) {
  return updateEmailRequest({
    id,
    updates: {
      status: 'archived'
    }
  });
}

async function getEmailRequestFiles({
  requestId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!requestId) {
    throw new Error(
      'Email request id is required.'
    );
  }

  const { data, error } =
    await db
      .from(
        'aa_email_request_files'
      )
      .select('*')
      .eq(
        'email_request_id',
        requestId
      )
      .order(
        'created_at',
        { ascending: true }
      );

  if (error) {
    throw error;
  }

  return data || [];
}

async function uploadEmailRequestFile({
  clientId,
  requestId,
  file
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!clientId) {
    throw new Error(
      'Client id is required.'
    );
  }

  if (!requestId) {
    throw new Error(
      'Email request id is required.'
    );
  }

  if (!file) {
    throw new Error(
      'Email request file is required.'
    );
  }

  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/pdf'
  ];

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      'File must be a JPG, PNG, WebP, or PDF.'
    );
  }

  const maxFileSize =
    20 * 1024 * 1024;

  if (file.size > maxFileSize) {
    throw new Error(
      'File must be 20 MB or smaller.'
    );
  }

  const extension =
    file.name
      .split('.')
      .pop()
      ?.toLowerCase() ||
    'file';

  const safeClientId =
    String(clientId)
      .replace(
        /[^a-zA-Z0-9-]/g,
        ''
      );

  const safeRequestId =
    String(requestId)
      .replace(
        /[^a-zA-Z0-9-]/g,
        ''
      );

  const storagePath =
    `${safeClientId}/` +
    `${safeRequestId}/` +
    `${Date.now()}-` +
    `${crypto.randomUUID()}.` +
    `${extension}`;

  const { error: uploadError } =
    await db.storage
      .from(
        'email-request-files'
      )
      .upload(
        storagePath,
        file,
        {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        }
      );

  if (uploadError) {
    throw uploadError;
  }

  const {
    data,
    error: recordError
  } =
    await db
      .from(
        'aa_email_request_files'
      )
      .insert({
        email_request_id:
          requestId,
        client_id:
          clientId,
        file_name:
          file.name,
        storage_path:
          storagePath,
        file_type:
          file.type
      })
      .select()
      .single();

  if (recordError) {
    try {
      await db.storage
        .from(
          'email-request-files'
        )
        .remove([
          storagePath
        ]);
    } catch (
      cleanupError
    ) {
      console.error(
        'Unable to clean up failed Email file upload:',
        cleanupError
      );
    }

    throw recordError;
  }

  return data;
}

async function getEmailRequestFileSignedUrl({
  path,
  expiresIn = 3600
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!path) {
    throw new Error(
      'Email request file path is required.'
    );
  }

  const {
    data,
    error
  } =
    await db.storage
      .from(
        'email-request-files'
      )
      .createSignedUrl(
        path,
        expiresIn
      );

  if (error) {
    throw error;
  }

  if (!data?.signedUrl) {
    throw new Error(
      'Unable to create file access URL.'
    );
  }

  return data.signedUrl;
}

async function removeEmailRequestFile({
  id
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Email request file id is required.'
    );
  }

  const {
    data: fileRecord,
    error: lookupError
  } =
    await db
      .from(
        'aa_email_request_files'
      )
      .select(
        'id, storage_path'
      )
      .eq('id', id)
      .single();

  if (lookupError) {
    throw lookupError;
  }

  const { error: storageError } =
    await db.storage
      .from(
        'email-request-files'
      )
      .remove([
        fileRecord.storage_path
      ]);

  if (storageError) {
    throw storageError;
  }

  const { error: deleteError } =
    await db
      .from(
        'aa_email_request_files'
      )
      .delete()
      .eq('id', id);

  if (deleteError) {
    throw deleteError;
  }

  return true;
}

async function getClientTemplateAssignments({ clientId } = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!clientId) {
    return [];
  }

  const { data: assignments, error: assignmentError } = await db
    .from('aa_client_template_assignments')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });

  if (assignmentError) {
    throw assignmentError;
  }

  if (!assignments?.length) {
    return [];
  }

  const templateIds = [
    ...new Set(
      assignments
        .map((assignment) => assignment.template_id)
        .filter(Boolean)
    )
  ];

  let templates = [];

  if (templateIds.length) {
    const { data, error } = await db
      .from('aa_workspace_templates')
      .select('*')
      .in('id', templateIds);

    if (error) {
      throw error;
    }

    templates = data || [];
  }

  return assignments.map((assignment) => {
    const template =
      templates.find(
        (item) => String(item.id) === String(assignment.template_id)
      ) || {};

    return {
      id: assignment.id,
      templateId: assignment.template_id,
      templateName:
        template.template_name ||
        template.name ||
        template.title ||
        'Unnamed Template',
      templateVersion:
        template.template_version ||
        template.version ||
        'v1',
      assignmentStatus:
        assignment.assignment_status ||
        assignment.status ||
        'Assigned'
    };
  });
}

return {
  init,
  getClient,
  getClients,
  createClient,
  updateClient,
  archiveClient,
  getClientConfiguration,
  saveClientConfiguration,
  getClientAccount,

  uploadCatalogCover,
  uploadPlaybookFile,
  getPlaybookFileSignedUrl,
  deletePlaybookFile,

  getCatalogRecords,
  getCatalogRecord,
  createCatalogRecord,
  updateCatalogRecord,
  archiveCatalogRecord,
  getPreviousMonthCatalog,

  getEmailRequests,
  getEmailRequest,
  createEmailRequest,
  updateEmailRequest,
  archiveEmailRequest,
  getEmailRequestFiles,
  uploadEmailRequestFile,
  getEmailRequestFileSignedUrl,
  removeEmailRequestFile,

  getClientTemplateAssignments,
  updateClientTemplateAssignmentStatus,
  deleteClientTemplateAssignment
};
})();